import { useRef, useState, useEffect } from "react";
import * as d3 from "d3";
import clsx from "clsx";
import { ChartHeader } from "@/components/ChartHeader";
import { Tooltip, useTooltip } from "@/components/Tooltip";
import { NoData } from "@/components/NoData";
import {
  formatMoney,
  makePossessive,
  getSectorDisplayName,
  formatMoneySr,
  formatPercentage,
} from "@/utils/format";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { getProductName, wrap } from "../utils";
import type { DataRow } from "../App";
import styles from "./styles.module.scss";

const xAxisHeight = 150;
const scaleMax = 10_000;

const topPadding = 20;

const regionInset = 2;

const tooltipWidthMobile = 360;
const tooltipWidth600 = 275;

const maxCircleRadius = 40;
const concentratedThreshold = 3_000;

function tspans(lines: string[], lineHeight?: string) {
  const lh = lineHeight || "1.3em";
  return lines.map((line, index) => (
    <tspan x="0" dy={index === 0 ? 0 : lh} key={index}>
      {line}
    </tspan>
  ));
}

interface BubbleProps {
  data: DataRow[];
  displayEconomy: string;
  sector: string;
  product: string;
  onProductChange: (newValue: string) => void;
  concentrationDescription?: string;
}

export function BubbleChart({
  data,
  displayEconomy,
  sector,
  product,
  onProductChange,
  concentrationDescription,
}: BubbleProps) {
  const is1200px = useMediaQuery("(min-width: 1200px)");
  const is1024px = useMediaQuery("(min-width: 1024px)");
  const is768px = useMediaQuery("(min-width: 768px)");
  const is600px = useMediaQuery("(min-width: 600px)");

  const yAxisLabelOffset = is1200px ? 80 : is1024px ? 105 : 120;
  const yAxisLabelHeight = is1200px ? 20 : is1024px ? 40 : 60;
  const height = (is1200px ? 1000 : 900) - xAxisHeight - yAxisLabelOffset;
  const yAxisWidth = is1200px ? 250 : is1024px ? 260 : 275;
  const yAxisTickLabelHeight = is1200px ? 12 : is1024px ? 14 : 18;
  const width = 1200 - yAxisWidth;
  const rightPaddingPixels = is768px ? 40 : 20;

  const annotationX = is1200px ? -100 : is1024px ? -110 : -120;

  const bubbleRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const [svgWidth, setSvgWidth] = useState(0);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      // Need to use target.clientWidth instead of contentRect.width because
      // the latter returns a different value on mobile safari
      setSvgWidth(entries[0].target.clientWidth);
    });
    if (svgRef.current) {
      resizeObserver.observe(svgRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  });

  const {
    showTooltip,
    moveTooltip,
    hideTooltip,
    tooltipProps,
    tooltipVisible,
    tooltipData,
  } = useTooltip<DataRow>(bubbleRef.current);

  const [
    isTopRightQuadrantTooltipVisible,
    setIsTopRightQuadrantTooltipVisible,
  ] = useState(false);

  const [
    isBottomRightQuadrantTooltipVisible,
    setIsBottomRightQuadrantTooltipVisible,
  ] = useState(false);

  const [
    isBottomLeftQuadrantTooltipVisible,
    setIsBottomLeftQuadrantTooltipVisible,
  ] = useState(false);

  if (data.length === 0) {
    return null;
  }

  const selectedProduct = data.find(
    (item) => item["Sub-sector code"] === product
  );

  // Sort data so selected product is last (for SVG layering)
  const sortedData = data
    .filter((item) => item["Sub-sector code"] !== product)
    .concat(selectedProduct || []);

  const circleRadiusScale = d3
    .scaleSqrt()
    .domain(d3.extent(data.map((d) => d["Total reporter value"])) as number[])
    .range([10, maxCircleRadius]);

  const rightPadding = svgWidth ? (rightPaddingPixels / svgWidth) * 1200 : 0;

  const xScale = d3
    .scaleLinear()
    .domain([0, scaleMax])
    .range([0, width - rightPadding]);

  const yScale = d3
    .scaleLinear()
    .domain([0, scaleMax])
    .range([height, topPadding]);

  const displaySector = getSectorDisplayName(sector);

  return (
    <div>
      <svg display="none" width="0" height="0">
        <defs>
          <symbol id="line-arrow" viewBox="0 0 18 18">
            <path d="M5.14645 4.82843L8.32843 1.64645C8.52369 1.45118 8.84027 1.45118 9.03553 1.64645L12.2175 4.82843C12.4128 5.02369 12.4128 5.34027 12.2175 5.53553C12.0223 5.7308 11.7057 5.7308 11.5104 5.53553L9.18198 3.20711L9.18198 17L8.18198 17L8.18198 3.20711L5.85355 5.53553C5.65829 5.73079 5.34171 5.73079 5.14645 5.53553C4.95118 5.34027 4.95118 5.02369 5.14645 4.82843Z" />
          </symbol>
          <symbol id="tooltip-icon" viewBox="0 0 11 11">
            <circle cx="5.5" cy="5.5" r="5.5" fill="currentColor" />
            <path
              d="M4.79812 2.94709C4.79812 2.52804 5.09611 2.2673 5.49654 2.2673C5.89696 2.2673 6.18564 2.52804 6.18564 2.94709C6.18564 3.36614 5.89696 3.63619 5.49654 3.63619C5.09611 3.63619 4.79812 3.36614 4.79812 2.94709ZM6.12045 8.15259C6.12045 8.30159 6.51156 8.66476 6.63262 8.66476H6.81886V9H4.23008V8.66476H4.41632C4.54669 8.66476 4.92849 8.30159 4.92849 8.15259V5.38688C4.92849 5.2472 4.54669 4.87471 4.41632 4.87471H4.23008V4.53947H6.12045V8.15259Z"
              fill="white"
            />
          </symbol>
        </defs>
      </svg>
      <ChartHeader
        title={` ${displayEconomy} ${displaySector.toLowerCase()} supply concentration for the top 20 products (by import value) `}
        description={concentrationDescription || ""}
      />

      {!sortedData.length ? (
        <NoData />
      ) : (
        <div ref={bubbleRef} className={styles.container}>
          <div className={styles["tooltip-container"]}>
            {tooltipVisible && tooltipData ? (
              <Tooltip
                {...tooltipProps}
                style={{ ...tooltipProps.style, width: is1024px ? 350 : 300 }}
              >
                <header className={styles["tooltip-header"]}>
                  <h4 className={styles["tooltip-heading"]}>
                    {getProductName(tooltipData["Sub-sector code"])}
                  </h4>
                  <p className={styles["tooltip-hs6-code"]}>
                    HS6 code: {tooltipData["Sub-sector code"]}
                  </p>
                </header>

                <ul className={styles["tooltip-list"]}>
                  <li>
                    <strong>
                      Top 3 suppliers account for{" "}
                      {formatPercentage(
                        tooltipData["Global top 3 share"] / 100
                      )}{" "}
                      of global imports
                    </strong>{" "}
                    (HHI: {tooltipData["Global HHI"].toLocaleString()})
                  </li>
                  <li>
                    <strong>
                      Top 3 suppliers account for{" "}
                      {formatPercentage(
                        tooltipData["Economy top 3 share"] / 100
                      )}{" "}
                      of {makePossessive(displayEconomy)} imports
                    </strong>{" "}
                    (HHI: {tooltipData["Economy-specific HHI"].toLocaleString()}
                    )
                  </li>
                  <li>
                    <strong>
                      Trade value:{" "}
                      {formatMoney(tooltipData["Total reporter value"])}
                    </strong>
                  </li>
                </ul>
              </Tooltip>
            ) : null}
          </div>
          <div className={styles.hide}>
            <p>{`${
              selectedProduct
                ? `${getProductName(
                    selectedProduct["Sub-sector code"]
                  )} is currently selected and has
            a total trade value of ${formatMoneySr(
              selectedProduct["Total reporter value"]
            )}, a global supply concentration of ${selectedProduct[
                    "Global HHI"
                  ].toLocaleString()} HHI (the top 3 suppliers account for ${formatPercentage(
                    selectedProduct["Global top 3 share"] / 100
                  )} of global imports) and supply concentration of ${displayEconomy} imports of ${selectedProduct[
                    "Economy-specific HHI"
                  ].toLocaleString()} HHI (the top 3 suppliers account for ${formatPercentage(
                    selectedProduct["Economy top 3 share"] / 100
                  )} of ${displayEconomy} imports). `
                : ""
            }${displayEconomy} has potential to diversify the following products
            (in other words, many suppliers exist globally but ${displayEconomy} imports from only a few), starting with the most
            concentrated products:`}</p>
            <ol>
              {data
                .filter(
                  (row) =>
                    row["Economy-specific HHI"] >= 3000 &&
                    row["Global HHI"] <= 3000
                )
                .sort(
                  (a, b) =>
                    b["Economy-specific HHI"] - a["Economy-specific HHI"]
                )
                .map((row) => (
                  <li key={row["Sub-sector code"]}>
                    {`${getProductName(
                      row["Sub-sector code"]
                    )} with a global supply concentration of ${row[
                      "Global HHI"
                    ].toLocaleString()} HHI (the top 3 suppliers account for ${formatPercentage(
                      row["Global top 3 share"] / 100
                    )} of global imports) and a supply concentration of ${displayEconomy} imports of ${row[
                      "Economy-specific HHI"
                    ].toLocaleString()} HHI (the top 3 suppliers account for ${formatPercentage(
                      row["Economy top 3 share"] / 100
                    )} of ${displayEconomy} imports)`}
                  </li>
                ))}
            </ol>
          </div>
          <svg
            className={styles.chart}
            viewBox={`0 0 ${width + yAxisWidth} ${
              height + xAxisHeight + yAxisLabelOffset
            }`}
            aria-hidden
            ref={svgRef}
          >
            <defs>
              <filter id="textBackground" x="0" y="0" width="1" height="1">
                <feFlood floodColor="white" result="bg" />
                <feMerge>
                  <feMergeNode in="bg" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
                <feComposite in="SourceGraphic" />
              </filter>
            </defs>
            {/* Y axis */}
            <g
              transform={`translate(${yAxisWidth},${yAxisLabelOffset})`}
              className={styles["axis"]}
            >
              <text
                className={styles["axis-label"]}
                transform={`translate(0, -${yAxisLabelHeight})`}
              >
                {tspans([`Global supply concentration`, `(HHI Index¹)`])}
              </text>

              <line
                x1={0}
                x2={0}
                y1={topPadding}
                y2={height}
                className={styles["axis-line"]}
              />

              {yScale.ticks(5).map((tick) => {
                const y = yScale(tick);
                return (
                  <g
                    key={tick}
                    transform={`translate(-10,${y})`}
                    textAnchor="end"
                    className={styles["tick"]}
                  >
                    <text x={-10} y={Math.round(yAxisTickLabelHeight / 2)}>
                      {tick.toLocaleString()}
                    </text>
                    <line
                      strokeWidth="2"
                      className={styles["axis-tickline"]}
                      x1={0}
                      x2={10}
                      y1="0"
                      y2="0"
                    />
                  </g>
                );
              })}
            </g>
            {/* Y axis annotations */}
            <g
              transform={`translate(${annotationX + yAxisWidth}, 
              ${-65 + yScale(concentratedThreshold) + yAxisLabelOffset})`}
            >
              <use
                transform="translate(11, 27)"
                className={styles["marker-arrow"]}
                height={is768px ? 18 : 16}
                width={is768px ? 18 : 16}
                xlinkHref="#line-arrow"
              />
              <text
                textAnchor="end"
                className={clsx(
                  styles["marker-text"],
                  styles["marker-text-bold-first"]
                )}
              >
                {tspans([
                  `More concentrated`,
                  `Fewer than ~3`,
                  `global suppliers`,
                ])}
              </text>
            </g>
            <g
              transform={`translate(${annotationX + yAxisWidth}, 
                ${30 + +yScale(concentratedThreshold) + yAxisLabelOffset})`}
            >
              <text
                textAnchor="end"
                className={clsx(
                  styles["marker-text"],
                  styles["marker-text-bold-first"]
                )}
              >
                {tspans([
                  `Less concentrated`,
                  `More than ~3`,
                  `global suppliers`,
                ])}
              </text>
              <use
                transform="translate(28, 8) rotate(180)"
                className={styles["marker-arrow"]}
                height={is768px ? 18 : 16}
                width={is768px ? 18 : 16}
                xlinkHref="#line-arrow"
              />
            </g>
            {/* X axis */}
            <g
              transform={`translate(${yAxisWidth}, ${
                height + yAxisLabelOffset
              })`}
              className={styles["axis"]}
            >
              <text
                className={styles["axis-label"]}
                textAnchor="end"
                transform={`translate(${width - rightPadding}, 85)`}
              >
                {tspans([
                  `Supply concentration of`,
                  `${displayEconomy} imports (HHI Index¹)`,
                ])}
              </text>

              <line
                x={0}
                x1={width - rightPadding}
                className={styles["axis-line"]}
              />
              {xScale.ticks(5).map((tick) => {
                const x = xScale(tick);
                return (
                  <g
                    key={tick}
                    transform={`translate(${x}, 0)`}
                    textAnchor={
                      x < 20 ? "start" : x > width - 100 ? "end" : "middle"
                    }
                    className={styles["tick"]}
                  >
                    <text y="40">{tick.toLocaleString()}</text>
                    <line
                      strokeWidth="2"
                      className={styles["axis-tickline"]}
                      x1="0"
                      x="0"
                      y1="0"
                      y2="10"
                    />
                  </g>
                );
              })}
            </g>
            {/* X axis annotations */}
            <g
              transform={`translate(
                ${-18 + xScale(concentratedThreshold) + yAxisWidth}, 
                ${height + yAxisLabelOffset + 85})`}
            >
              <use
                transform="translate(-18, -20) rotate(-90)"
                className={styles["marker-arrow"]}
                height={is768px ? 18 : 16}
                width={is768px ? 18 : 16}
                xlinkHref="#line-arrow"
              />
              <text
                textAnchor="end"
                className={clsx(
                  styles["marker-text"],
                  styles["marker-text-bold-first"]
                )}
              >
                {tspans([
                  `Less concentrated`,
                  `More than ~3 suppliers`,
                  `to ${displayEconomy}`,
                ])}
              </text>
            </g>
            <g
              transform={`translate(${
                18 + xScale(concentratedThreshold) + yAxisWidth
              }, ${height + yAxisLabelOffset + 85})`}
            >
              <use
                transform="translate(18, -37) rotate(90)"
                className={styles["marker-arrow"]}
                height={is768px ? 18 : 16}
                width={is768px ? 18 : 16}
                xlinkHref="#line-arrow"
              />
              <text
                textAnchor="start"
                className={clsx(
                  styles["marker-text"],
                  styles["marker-text-bold-first"]
                )}
              >
                {tspans([
                  `More concentrated`,
                  `Fewer than ~3 suppliers`,
                  `to ${displayEconomy} `,
                ])}
              </text>
            </g>

            {/* Rectangle Highlight */}
            <g transform={`translate(${yAxisWidth},0)`}>
              <rect
                className={styles["region-highlight-local"]}
                x={xScale(concentratedThreshold) + regionInset}
                width={xScale(scaleMax - concentratedThreshold) - regionInset}
                y={
                  yAxisLabelOffset + yScale(concentratedThreshold) + regionInset
                }
                height={
                  height - yScale(concentratedThreshold) - 2 * regionInset
                }
              />
            </g>

            {/* Vert line at 3000 */}
            <line
              className={styles["marker-line"]}
              x1={xScale(concentratedThreshold) + yAxisWidth}
              x2={xScale(concentratedThreshold) + yAxisWidth}
              y1={yScale(scaleMax) + yAxisLabelOffset}
              y2={130 + height + yAxisLabelOffset}
              strokeDasharray="4 2"
            />

            {/* Horiz line at 3000 */}
            <line
              className={styles["marker-line"]}
              x1={-220 + yAxisWidth}
              x2={xScale(scaleMax) + yAxisWidth}
              y1={yScale(concentratedThreshold) + yAxisLabelOffset}
              y2={yScale(concentratedThreshold) + yAxisLabelOffset}
              strokeDasharray="4 2"
            />

            {/* Bubbles */}
            <g transform={`translate(${yAxisWidth},${yAxisLabelOffset})`}>
              {sortedData.map((d) => {
                const x = xScale(d["Economy-specific HHI"]);
                const y = yScale(d["Global HHI"]);

                const isSelected = d["Sub-sector code"] === product;

                // Get full product name and convert to array of lines ~20 chars long each
                const prodName = getProductName(d["Sub-sector code"]);
                const prodNamelines = wrap(prodName || "", 30, 2);
                const circleR = circleRadiusScale(d["Total reporter value"]);

                const economyHHI = d["Economy-specific HHI"];
                const globalHHI = d["Global HHI"];

                const bubbleClassName =
                  globalHHI > concentratedThreshold &&
                  economyHHI > concentratedThreshold
                    ? "bubble-global"
                    : economyHHI > concentratedThreshold
                    ? "bubble-local"
                    : "bubble-normal";

                const isOnRightEdge =
                  (d["Economy-specific HHI"] >= 9500 &&
                    !(prodName && prodName.length <= 5)) ||
                  (d["Economy-specific HHI"] >= 9000 &&
                    !(prodName && prodName.length <= 10)) ||
                  (d["Economy-specific HHI"] >= 8500 &&
                    !(prodName && prodName.length <= 15));

                return (
                  <g
                    className={clsx(
                      styles.bubble,
                      styles[bubbleClassName],
                      isSelected && styles["bubble-selected"]
                    )}
                    transform={`translate(${x},0)`}
                    key={`${d["Economy-specific HHI"]}-${d["Sub-sector code"]}`}
                    onClick={() => {
                      onProductChange(d["Sub-sector code"]);
                    }}
                    onMouseEnter={(event) => {
                      showTooltip(event, d);
                    }}
                    onMouseMove={(event) => {
                      moveTooltip(event);
                    }}
                    onMouseLeave={() => {
                      hideTooltip();
                    }}
                  >
                    {isSelected ? (
                      <g className={styles["bubble-annotation"]}>
                        <text
                          filter="url(#textBackground)"
                          transform={`translate(${
                            isOnRightEdge ? xScale(scaleMax) - x : 0
                          }, ${y + circleR})`}
                          textAnchor={isOnRightEdge ? "end" : "middle"}
                          className={styles["bubble-label"]}
                        >
                          {prodNamelines.map((line, i) => (
                            <tspan key={i} x={0} dy="1.25em">
                              {line}
                            </tspan>
                          ))}
                        </text>
                      </g>
                    ) : null}

                    <circle
                      className={styles["bubble-circle"]}
                      r={circleR}
                      cy={y}
                    />
                  </g>
                );
              })}
            </g>

            {/* Quadrant annotations */}

            <g transform={`translate(${yAxisWidth},${yAxisLabelOffset})`}>
              {isTopRightQuadrantTooltipVisible ? (
                <g
                  transform={`translate(${
                    xScale(scaleMax) -
                    (is600px ? tooltipWidth600 : tooltipWidthMobile)
                  }, 20)`}
                  className={styles["quadrant-tooltip"]}
                >
                  <rect
                    fill="white"
                    x={-1}
                    y={1}
                    width={is600px ? tooltipWidth600 : tooltipWidthMobile}
                    height={is600px ? 100 : 110}
                    className={styles["quadrant-tooltip-bg"]}
                  />
                  <text
                    transform="translate(25,70)"
                    className={styles["quadrant-tooltip-text"]}
                  >
                    Few suppliers exist globally
                  </text>
                </g>
              ) : null}

              <g
                className={styles["quadrant-label"]}
                transform={`translate(
                  ${
                    25 +
                    xScale(scaleMax) -
                    (is600px ? tooltipWidth600 : tooltipWidthMobile)
                  }
                , 45)`}
                onMouseEnter={() => {
                  setIsTopRightQuadrantTooltipVisible(true);
                }}
                onMouseLeave={() => {
                  setIsTopRightQuadrantTooltipVisible(false);
                }}
              >
                <text x={25} y={15} className={styles["quadrant-label-text"]}>
                  Globally concentrated
                </text>
                <use
                  className={styles["quadrant-label-icon"]}
                  height={is768px ? 18 : 16}
                  width={is768px ? 18 : 16}
                  xlinkHref="#tooltip-icon"
                />
              </g>

              {isBottomRightQuadrantTooltipVisible ? (
                <g
                  transform={`translate(${
                    xScale(scaleMax) -
                    (is600px ? tooltipWidth600 : tooltipWidthMobile)
                  }, ${1 + yScale(concentratedThreshold)})`}
                  className={styles["quadrant-tooltip"]}
                >
                  <rect
                    fill="white"
                    x={-1}
                    y={1}
                    width={is600px ? tooltipWidth600 : tooltipWidthMobile}
                    height={is600px ? 140 : 150}
                    className={styles["quadrant-tooltip-bg"]}
                  />
                  <text
                    transform="translate(25,70)"
                    className={styles["quadrant-tooltip-text"]}
                  >
                    {tspans(
                      [
                        `Many suppliers exist globally`,
                        `but ${displayEconomy}`,
                        `imports from only a few`,
                      ],
                      "1.2em"
                    )}
                  </text>
                </g>
              ) : null}

              <g
                className={styles["quadrant-label"]}
                transform={`translate(
                ${
                  25 +
                  xScale(scaleMax) -
                  (is600px ? tooltipWidth600 : tooltipWidthMobile)
                }, 
                ${25 + yScale(concentratedThreshold)}
              )`}
                onMouseEnter={() => {
                  setIsBottomRightQuadrantTooltipVisible(true);
                }}
                onMouseLeave={() => {
                  setIsBottomRightQuadrantTooltipVisible(false);
                }}
              >
                <text x={25} y={15} className={styles["quadrant-label-text"]}>
                  Locally concentrated
                </text>
                <use
                  className={styles["quadrant-label-icon"]}
                  height={is768px ? 18 : 16}
                  width={is768px ? 18 : 16}
                  xlinkHref="#tooltip-icon"
                />
              </g>

              {isBottomLeftQuadrantTooltipVisible ? (
                <g
                  transform={`translate(0, ${
                    1 + yScale(concentratedThreshold)
                  } )`}
                  className={styles["quadrant-tooltip"]}
                >
                  <rect
                    fill="red"
                    x={1}
                    y={1}
                    width={is600px ? tooltipWidth600 : tooltipWidthMobile}
                    height={is600px ? 140 : 150}
                    className={styles["quadrant-tooltip-bg"]}
                  />
                  <text
                    transform="translate(25,70)"
                    className={styles["quadrant-tooltip-text"]}
                  >
                    {tspans(
                      [
                        `Many suppliers exist globally`,
                        `and ${displayEconomy}`,
                        `imports from many of them`,
                      ],
                      "1.2em"
                    )}
                  </text>
                </g>
              ) : null}

              <g
                className={styles["quadrant-label"]}
                transform={`translate(25, ${25 + yScale(concentratedThreshold)}
                  )`}
                onMouseEnter={() => {
                  setIsBottomLeftQuadrantTooltipVisible(true);
                }}
                onMouseLeave={() => {
                  setIsBottomLeftQuadrantTooltipVisible(false);
                }}
              >
                <text x={25} y={15} className={styles["quadrant-label-text"]}>
                  Not concentrated
                </text>
                <use
                  className={styles["quadrant-label-icon"]}
                  height={is768px ? 18 : 16}
                  width={is768px ? 18 : 16}
                  xlinkHref="#tooltip-icon"
                />
              </g>
            </g>
          </svg>
        </div>
      )}
    </div>
  );
}
